/** Copyright (C) 2022 Trend Micro Inc. All rights reserved. */

import {
  createSlice,
  createSelector,
  PayloadAction,
  createAsyncThunk,
} from '@reduxjs/toolkit'
import statisticsApi from '@redux/apis/statistics'
import { scanHistoryInterval } from '@utils/scanHistory'
import { hourFormatConvert, getTimeZoneDateInstance } from '@utils/time'
import { MONTH_ABBR } from '@constants/time'

const REDUCER_NAME = 'dashboard'

interface FetchEventsActionParams
  extends Request.StatisticsGetScanResultParams {
  fetchMoreType: Redux.ScanResultFetchDirection
}

interface FetchEventsActionResult extends FetchEventsActionParams {
  data: Response.Statistics.ScanResultItem[]
  c1UserTimeZone: string
}

const initDashboardState: Redux.DashboardState = {
  scanResult: {
    collection: [],
    barChartData: [],
    isLoading: false,
    fetchMoreType: 'later',
    interval: 'hour',
    hasError: false,
  },
  chartDisplayScanRange: {
    from: null,
    to: null,
  },
  enableScanTypes: {
    isMaliciousEnable: true,
    isScanEnable: true,
    isScanErrorEnable: true,
  },
}

function getFormattedScanReducerData(
  newSource: Response.Statistics.ScanResultItem[] = [],
  existCollection: Response.Statistics.ScanResultItem[],
  interval: Redux.ScanResultInterval,
  fetchMoreType: Redux.ScanResultFetchDirection,
  from: string,
  getDateHelper = (dateString?: string) => new Date(dateString)
) {
  if (!from) {
    return {
      collection: [],
      barChartData: [],
    }
  }

  const map: Record<number | string, Response.Statistics.ScanResultItem> = {}
  const f = getDateHelper(from)
  const collectionChunk: Response.Statistics.ScanResultItem[] = []
  const barChartDataChunk = []
  const emptyScanItem: Response.Statistics.ScanResultItem = {
    scans: 0,
    detections: 0,
    failed: 0,
    from: '',
    interval: interval === 'hour' ? '1h' : '1d',
  }
  const emptyChartDataItem: Redux.BarChartItem = {
    x: '',
    y: [0, 0, 0],
  }
  // Insert an default value to the X axis doesn't contain data.
  for (let j = 0; j < newSource.length; j++) {
    const t = getDateHelper(newSource[j].from)

    map[
      interval === 'hour'
        ? `${t.getDate()}:${t.getHours()}`
        : `${t.getMonth() + 1}:${t.getDate()}`
    ] = newSource[j]
  }

  for (let i = 0; i < scanHistoryInterval(interval); i++) {
    let item
    let chartDataItemTime

    if (
      existCollection.length === 0 ||
      (fetchMoreType === 'earlier' &&
        f <= getDateHelper(existCollection?.[0]?.from)) ||
      (fetchMoreType === 'later' &&
        f >= getDateHelper(existCollection?.[existCollection.length - 1]?.from))
    ) {
      if (interval === 'hour') {
        item = map[`${f.getDate()}:${f.getHours()}`]
        chartDataItemTime =
          f.getHours() !== 0
            ? `${hourFormatConvert(f.getHours())}:00`
            : `${MONTH_ABBR[f.getMonth()]} ${f.getDate()}`
      } else {
        item = map[`${f.getMonth() + 1}:${f.getDate()}`]

        const prevDate: Date =
          collectionChunk[i - 1] && getDateHelper(collectionChunk[i - 1].from)

        chartDataItemTime =
          prevDate && prevDate.getFullYear() !== f.getFullYear()
            ? `${f.getFullYear()}`
            : `${MONTH_ABBR[f.getMonth()]} ${f.getDate()}`
      }

      emptyScanItem.from = f.toISOString()
      emptyChartDataItem.x = chartDataItemTime

      collectionChunk[i] = item || { ...emptyScanItem }
      barChartDataChunk[i] = item
        ? {
            x: chartDataItemTime,
            y: [
              item.scans - item.detections - item.failed,
              item.failed,
              item.detections,
            ],
          }
        : { ...emptyChartDataItem }
    }

    if (interval === 'hour') {
      f.setHours(f.getHours() + 1)
    } else {
      f.setDate(f.getDate() + 1)
    }

    if (f > getDateHelper()) {
      break
    }
  }

  return {
    collection: collectionChunk,
    barChartData: barChartDataChunk,
  }
}

function setDashboardDisplayScanTimeRangeAction(
  state: typeof initDashboardState,
  action: PayloadAction<Redux.ChartDisplayScanRange>
) {
  state.chartDisplayScanRange.from = action.payload.from
  state.chartDisplayScanRange.to = action.payload.to
}

function toggleEnableScanTypesAction(
  state: typeof initDashboardState,
  action: PayloadAction<Redux.EnableScanType>
) {
  state.enableScanTypes.isMaliciousEnable = action.payload.isMaliciousEnable
  state.enableScanTypes.isScanEnable = action.payload.isScanEnable
  state.enableScanTypes.isScanErrorEnable = action.payload.isScanErrorEnable
}

function resetStatisticAction() {
  return initDashboardState
}

export const fetchScanResult = createAsyncThunk<
  FetchEventsActionResult,
  FetchEventsActionParams
>(
  `${REDUCER_NAME}/fetchScanResult`,
  async (
    { from, to, interval, cursor, limit, fetchMoreType },
    { dispatch, getState }
  ) => {
    const {
      common: { c1UserTimeZone },
    } = getState() as Redux.RootState
    const result: Response.Statistics.ScanResultItem[] = []

    const res = await dispatch(
      statisticsApi.endpoints.getScanResult.initiate({
        from,
        to,
        interval,
        cursor,
        limit,
      })
    ).unwrap()

    const rawData = res?.statistics || []
    const { next } = res || {}

    result.push(...rawData)

    if (cursor && rawData?.length > 0) {
      const recursiveStatisticsResult = await dispatch(
        fetchScanResult({
          from,
          to,
          interval,
          limit,
          cursor: next,
          fetchMoreType,
        })
      ).unwrap()
      const consecutiveResult = recursiveStatisticsResult.data || []

      result.push(...consecutiveResult)
    }

    return {
      data: result,
      from,
      to,
      interval,
      cursor,
      limit,
      fetchMoreType,
      c1UserTimeZone,
    }
  }
)

const dashboardSlice = createSlice({
  name: REDUCER_NAME,
  initialState: initDashboardState,
  reducers: {
    setDashboardDisplayScanTimeRange: setDashboardDisplayScanTimeRangeAction,
    toggleEnableScanTypes: toggleEnableScanTypesAction,
    resetDashboard: resetStatisticAction,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchScanResult.pending, (state, action) => {
        if (action.meta.arg.interval !== state.scanResult.interval) {
          state.scanResult.collection = []
          state.scanResult.barChartData = []
        }
        state.scanResult.interval = action.meta.arg.interval
        state.scanResult.fetchMoreType = action.meta.arg.fetchMoreType
        state.scanResult.isLoading = true
      })
      .addCase(fetchScanResult.fulfilled, (state, action) => {
        const {
          scanResult: { collection },
        } = state
        const getC1UserTzDateInstance = getTimeZoneDateInstance(
          action.payload.c1UserTimeZone
        )
        const { collection: nextCollection, barChartData: nextBarChartData } =
          getFormattedScanReducerData(
            action.payload.data,
            collection,
            action.payload.interval,
            action.payload.fetchMoreType,
            action.payload.from,
            getC1UserTzDateInstance
          )

        state.scanResult.interval = action.meta.arg.interval
        state.scanResult.fetchMoreType = action.meta.arg.fetchMoreType

        state.scanResult.collection =
          state.scanResult.fetchMoreType === 'earlier'
            ? [...nextCollection, ...state.scanResult.collection]
            : [...state.scanResult.collection, ...nextCollection]
        state.scanResult.barChartData =
          state.scanResult.fetchMoreType === 'earlier'
            ? [...nextBarChartData, ...state.scanResult.barChartData]
            : [...state.scanResult.barChartData, ...nextBarChartData]

        state.scanResult.isLoading = false
        state.scanResult.hasError = false
      })
      .addCase(fetchScanResult.rejected, state => {
        state.scanResult.isLoading = false
        state.scanResult.hasError = true
      })
  },
})

const selectSelf = (state: Redux.RootState) => state.dashboard

export const scanResultSelector = createSelector(
  selectSelf,
  dashboard => dashboard.scanResult
)

export const chartDisplayScanRangeSelector = createSelector(
  selectSelf,
  dashboard => dashboard.chartDisplayScanRange
)

export const enableScanTypesSelector = createSelector(
  selectSelf,
  dashboard => dashboard.enableScanTypes
)

export const {
  setDashboardDisplayScanTimeRange,
  toggleEnableScanTypes,
  resetDashboard,
} = dashboardSlice.actions

export default dashboardSlice.reducer
